'use client'

import DewiWorldBanner from '@assets/DewiWorldBanner'
import { AirclawButton } from '@components/AirclawButton'
import { ChangeEvent, FormEvent, useCallback, useState } from 'react'
import JSConfetti from 'js-confetti'
import { cn } from '@utils/index'
import { useWallet } from '@solana/wallet-adapter-react'
import { useAsync } from 'react-async-hook'
import Button from '@components/Furry/Button'
import Image from 'next/image'

const COLORS = [
  '#0F1A1C', // background
  '#7DDDFF', // foreground
  '#163238', // background-raised
  '#1A2426', // card
  '#DFFFFF', // card-foreground
  '#0A0A0A', // popover
  '#FAFAFA', // popover-foreground
  '#FAFAFA', // primary
  '#171717', // primary-foreground
  '#262626', // secondary
  '#FAFAFA', // secondary-foreground
  '#007399', // muted
  '#DFFFFF', // muted-foreground
]

const STATUS = {
  alreadyEnrolled: 'alreadyEnrolled',
  notEnrolled: 'notEnrolled',
  loading: 'loading',
  error: 'error',
} as const

const slant = 'calc(100% - 40px)'

type DewiWorldEmailSubmitProps = {
  className?: string
}

export const DewiWorldEmailSubmit = ({
  className,
}: DewiWorldEmailSubmitProps) => {
  const jsConfetti = new JSConfetti()
  const [status, setStatus] = useState<keyof typeof STATUS>(STATUS.notEnrolled)
  const [email, setEmail] = useState('')
  const { publicKey } = useWallet()
  const [loading, setLoading] = useState(true)

  useAsync(async () => {
    setLoading(true)
    const res = await fetch(
      `/api/verifyDewiWorld?dewiAddress=${publicKey.toBase58()}`,
    )
    const data = await res.json()
    console.log('data', data)

    if (res.ok) {
      setStatus(STATUS.alreadyEnrolled)
    }
    setLoading(false)
  }, [publicKey])

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      // Handle submission
      setLoading(true)
      try {
        const res = await fetch('/api/registerDewiWorld', {
          method: 'POST',
          body: JSON.stringify({
            dewiAddress: publicKey.toBase58(),
            email,
          }),
        })

        if (res.ok) {
          setStatus(STATUS.alreadyEnrolled)
          setEmail('')
          await jsConfetti.addConfetti({ confettiColors: COLORS })
          return
        }

        const error = await res.json()
        throw new Error(error.message || `Error: ${res.status}`)
      } catch (err) {
        console.error(err)
        setStatus(STATUS.error)
      } finally {
        setLoading(false)
      }
    },
    [email, publicKey, jsConfetti],
  )

  return (
    <div
      className={cn(
        'bg-[#FFFFFF08] relative w-full max-w-80 p-4 flex flex-col items-center gap-4 rounded-lg border-2 border-[#2E2E2E] pt-8',
        className,
      )}
    >
      <Image
        alt="dewi-world-logo"
        src="/assets/dewi-world-banner3.png"
        width={150}
        style={{
          objectFit: 'cover',
          width: 290,
          height: 190,
          borderRadius: 20,
          marginBottom: 5,
        }}
        height={150}
      />
      <DewiWorldBanner width={200} height={50} />
      <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-2 w-full">
        <input
          type="email"
          required
          disabled={loading}
          className="w-full bg-[#FFFFFF11] text-[white] focus-visible:outline-[#2E2E2E] rounded-md border-[#2E2E2E] border-2 px-4 py-2 flex justify-start"
          placeholder={
            status === STATUS.notEnrolled
              ? 'Register your email'
              : 'Change your email'
          }
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setEmail(e.target.value)
          }}
        />
        <Button
          type="submit"
          disabled={!email}
          className="disabled:opacity-50 m-auto uppercase min-w-24"
        >
          {loading ? (
            <LoadingSpinner />
          ) : status === STATUS.notEnrolled ? (
            'Enroll'
          ) : (
            'Update'
          )}
        </Button>
      </form>
    </div>
  )
}

const LoadingSpinner = () => {
  return (
    <div role="status">
      <svg
        aria-hidden="true"
        className="inline w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-[#AEAEAE]"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
      <span className="sr-only">Loading...</span>
    </div>
  )
}
